
class ExxtraBookingSummary {

    constructor() {
        this.storageKey = "exxtra-booking-summary";

        this.loadFields();
        this.bindInputs();

        // Refresh whenever duration, quantity or payment plan changes.
        document.addEventListener(
            "valet:bookingUpdated",
            () => {
                this.updatePricing();
                this.updateAddressSummary();
                this.updateElevatorCharge();
            }
        );

        this.updateAddressSummary();
        this.updatePricing();
        this.updateElevatorCharge();
    }

    /**
     * Bind all summary inputs.
     */
    bindInputs() {
        const fields = document.querySelectorAll(
            "[data-summary-source]"
        );

        fields.forEach(field => {
            field.addEventListener("input", () => {
                this.updateField(field);
            });

            field.addEventListener("change", () => {
                this.updateField(field);
            });

            this.updateField(field);
        });
    }

    /**
     * Update a summary field.
     */
    updateField(field) {
        const key = field.dataset.summarySource;
        let value = "";

        switch (field.type) {
            case "checkbox":
                value = field.checked ? "Yes" : "No";
                break;

            case "radio":
                if (!field.checked) return;

                value =
                    field.dataset.summaryValue ||
                    field.value;

                break;

            default:
                if (field.tagName === "SELECT") {
                    value =
                        field.options[field.selectedIndex]?.text || "";
                } else {
                    value = (field.value || "").trim();
                }
        }

        if (!value) value = "—";

        document
            .querySelectorAll(`[data-summary="${key}"]`)
            .forEach(summary => {
                summary.textContent = value;
            });

        this.saveField(key, value);
        this.updateBookingState();
        this.updateAddressSummary();
        this.updatePricing();
        this.updateElevatorCharge();
    }

    /**
     * Save summary fields.
     */
    saveField(key, value) {
        let data = {};

        try {
            data =
                JSON.parse(
                    localStorage.getItem(this.storageKey)
                ) || {};
        } catch (error) {
            data = {};
        }

        data[key] = value;

        localStorage.setItem(
            this.storageKey,
            JSON.stringify(data)
        );
    }

    /**
     * Load saved summary fields.
     */
    loadFields() {
        let data = {};

        try {
            data =
                JSON.parse(
                    localStorage.getItem(this.storageKey)
                ) || {};
        } catch (error) {
            data = {};
        }

        Object.keys(data).forEach(key => {
            document
                .querySelectorAll(`[data-summary="${key}"]`)
                .forEach(summary => {
                    summary.textContent = data[key];
                });
        });
    }

    /**
     * Format cents as SGD.
     */
    formatMoney(cents) {
        return new Intl.NumberFormat("en-SG", {
            style: "currency",
            currency: "SGD"
        }).format(Number(cents || 0) / 100);
    }

    /**
     * Format a dollar amount.
     */
    formatAmount(amount) {
        return "$" + Number(amount || 0).toFixed(2);
    }

    /**
     * Parse a product price.
     *
     * Examples:
     * "$7"       -> 7
     * "$4.50"    -> 4.5
     * "SGD 7.00" -> 7
     */
    parsePrice(value) {
        const cleaned = String(value ?? "")
            .replace(/,/g, "")
            .replace(/[^\d.-]/g, "");

        const amount = Number(cleaned);

        return Number.isFinite(amount) ? amount : 0;
    }

    /**
     * Determine the selected storage duration.
     */
    getStorageTermMonths(booking) {
        const duration = String(
            booking.duration || ""
        ).trim().toLowerCase();

        if (
            duration === "monthly" ||
            duration === "month"
        ) {
            return 1;
        }

        const match = duration.match(
            /(\d+)\s*[-–— ]?\s*months?/i
        );

        if (match) {
            return Math.max(1, Number(match[1]));
        }

        const fallback = Number(booking.months || 1);

        return fallback > 0 ? fallback : 1;
    }

    /**
     * Get the selected Seal payment interval.
     *
     * Examples:
     * "month"    -> 1
     * "monthly"  -> 1
     * "3-month"  -> 3
     * "3 months" -> 3
     * "6-month"  -> 6
     * "12-month" -> 12
     */
    getPaymentIntervalMonths(booking) {
        const interval = String(
            booking.paymentInterval || "month"
        ).trim().toLowerCase();

        if (
            interval === "month" ||
            interval === "monthly"
        ) {
            return 1;
        }

        const match = interval.match(
            /(\d+)\s*[-–— ]?\s*months?/i
        );

        if (match) {
            return Math.max(1, Number(match[1]));
        }

        return 1;
    }

    /**
     * Update booking state from address inputs.
     */
    updateBookingState() {
        const booking = window.valetBooking;

        if (!booking) return;

        if (!booking.address) {
            booking.address = {};
        }

        booking.address.fullName =
            document.querySelector(
                '[data-summary-source="full-name"]'
            )?.value || "";

        booking.address.phone =
            document.querySelector(
                '[data-summary-source="phone"]'
            )?.value || "";

        booking.address.line1 =
            document.querySelector(
                '[data-summary-source="address-line-1"]'
            )?.value || "";

        booking.address.line2 =
            document.querySelector(
                '[data-summary-source="address-line-2"]'
            )?.value || "";

        booking.address.unit =
            document.querySelector(
                '[data-summary-source="unit-number"]'
            )?.value || "";

        booking.address.postcode =
            document.querySelector(
                '[data-summary-source="postal-code"]'
            )?.value || "";
    }

    /**
     * Update address summary.
     */
    updateAddressSummary() {
        const booking = window.valetBooking;

        if (!booking?.address) return;

        const address = booking.address;
        const parts = [];

        if (address.fullName) {
            parts.push(address.fullName);
        }

        if (address.line1) {
            parts.push(address.line1);
        }

        if (address.line2) {
            parts.push(address.line2);
        }

        if (address.unit) {
            parts.push(address.unit);
        }

        if (address.postcode) {
            parts.push("Singapore " + address.postcode);
        }

        document
            .querySelectorAll(
                '[data-summary="delivery-address"]'
            )
            .forEach(summary => {
                summary.replaceChildren();

                if (!parts.length) {
                    summary.textContent = "—";
                    return;
                }

                parts.forEach((part, index) => {
                    if (index > 0) {
                        summary.appendChild(
                            document.createElement("br")
                        );
                    }

                    summary.appendChild(
                        document.createTextNode(part)
                    );
                });
            });
    }

    /**
     * Update elevator charge.
     */
    updateElevatorCharge() {
        const charge =
            window.valetBooking?.elevatorCharge;

        const row = document.querySelector(
            "[data-summary-elevator]"
        );

        const divider = document.querySelector(
            "[data-summary-elevator-divider]"
        );

        if (!row) return;

        if (!charge || !charge.enabled) {
            row.hidden = true;

            if (divider) {
                divider.hidden = true;
            }

            return;
        }

        row.hidden = false;

        if (divider) {
            divider.hidden = false;
        }

        const title = row.querySelector(
            "[data-summary-elevator-title]"
        );

        const price = row.querySelector(
            "[data-summary-elevator-price]"
        );

        if (title) {
            title.textContent = charge.title;
        }

        if (price) {
            price.textContent = charge.price;
        }
    }

    /**
     * Update Valet Storage pricing.
     *
     * Assumption based on the current product configuration:
     * variant price = price per crate per month.
     *
     * Monthly billing:
     * price per crate/month × crates
     *
     * Total contract commitment:
     * monthly charge × storage duration
     *
     * Prepaid:
     * total contract commitment × (1 - discount rate)
     *
     * Discount:
     * 3-month = 3%
     * 6-month  = 3%
     * 12-month = 5%
     * Monthly = no prepaid discount
     */
    updatePricing() {
        const booking = window.valetBooking;

        if (!booking) return;

        // Price per crate per month for the selected variant.
        const pricePerCratePerMonth = this.parsePrice(
            booking.monthlyCost
        );

        // Respect the selected variant's minimum quantity.
        const minimumQuantity = Math.max(
            1,
            Number(booking.minimumQuantity || 1)
        );

        const crates = Math.max(
            minimumQuantity,
            Number(booking.crates || 1)
        );

        // Selected contract duration.
        const termMonths =
            this.getStorageTermMonths(booking);

        // Selected Seal billing interval.
        const paymentIntervalMonths =
            this.getPaymentIntervalMonths(booking);

        // Selecting a full-term interval means prepaid.
        const isPrepaid =
            termMonths > 1 &&
            paymentIntervalMonths === termMonths;

        // Amount charged for one month.
        const monthlyTotal = Math.round(
            pricePerCratePerMonth * crates * 100
        ) / 100;

        // Full contract commitment before discount.
        const contractTotal = Math.round(
            monthlyTotal * termMonths * 100
        ) / 100;

        // Calculate prepaid discount by contract duration.
        let discountRate = 0;

        if (termMonths === 3 || termMonths === 6) {
            discountRate = 0.03;
        } else if (termMonths === 12) {
            discountRate = 0.05;
        }

        // Apply discount only when the prepaid plan is selected.
        const discountAmount = isPrepaid
            ? Math.round(
                contractTotal * discountRate * 100
            ) / 100
            : 0;

        const prepaidTotal = Math.round(
            (contractTotal - discountAmount) * 100
        ) / 100;

        // Main price shown in the booking summary.
        const displayedTotal = isPrepaid
            ? prepaidTotal
            : monthlyTotal;

        const costLabel = isPrepaid
            ? "Prepaid Total"
            : "Monthly Cost";

        const billingSuffix = isPrepaid
            ? `/${termMonths} months`
            : "/mo";

        // Update crate quantity in the summary.
        document
            .querySelectorAll(
                '[data-summary="crate-quantity"]'
            )
            .forEach(item => {
                item.textContent = crates;
            });

        // Update price, label, suffix and contract information.
        document
            .querySelectorAll(
                '[data-summary="monthly-price"]'
            )
            .forEach(item => {
                item.textContent =
                    this.formatAmount(displayedTotal);

                const costBlock = item.closest(
                    ".valet-summary__cost"
                );

                if (!costBlock) return;

                const label = costBlock.querySelector(
                    "span"
                );

                const suffix = costBlock.querySelector(
                    "small"
                );

                if (label) {
                    label.textContent = costLabel;
                }

                if (suffix) {
                    suffix.textContent = billingSuffix;
                }

                // Add a separate line explaining the full commitment
                // when customers choose monthly billing.
                let commitmentNote = costBlock.querySelector(
                    "[data-summary-commitment]"
                );

                if (!commitmentNote) {
                    commitmentNote =
                        document.createElement("small");

                    commitmentNote.className =
                        "valet-summary__commitment";

                    commitmentNote.dataset.summaryCommitment = "";

                    commitmentNote.style.fontSize = "12px";
                    commitmentNote.style.lineHeight = "1.4";
                    commitmentNote.style.marginTop = "6px";
                    commitmentNote.style.color = "inherit";

                    costBlock.appendChild(commitmentNote);
                }

                if (termMonths > 1 && isPrepaid) {
                    commitmentNote.hidden = false;

                    commitmentNote.textContent =
                        `You save ${this.formatAmount(discountAmount)} (${discountRate * 100}% off)`;
                } else if (termMonths > 1) {
                    commitmentNote.hidden = false;

                    commitmentNote.textContent =
                        `Total commitment: ${this.formatAmount(contractTotal)} over ${termMonths} months`;
                } else {
                    commitmentNote.hidden = true;
                    commitmentNote.textContent = "";
                }
            });

        console.log("[Valet Pricing]", {
            duration: booking.duration,
            variantId: booking.variantId,
            pricePerCratePerMonth,
            crates,
            termMonths,
            paymentInterval: booking.paymentInterval,
            paymentIntervalMonths,
            paymentType: isPrepaid ? "prepaid" : "monthly",
            monthlyTotal,
            contractTotal,
            discountRate,
            discountAmount,
            prepaidTotal,
            displayedTotal
        });
    }
}

/**
 * Initialise booking summary.
 */
document.addEventListener("DOMContentLoaded", () => {
    window.exxtraBookingSummary =
        new ExxtraBookingSummary();
});
